import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { LifecyclesService } from '../../common/services/lifecycle/lifecycle.service';
import { UserIdentityService } from '../../common/services/userIdentity/user-identity.service';
import * as AppLifecyclesActions from './app-lifecycles.actions';

@Injectable()
export class AppLifecyclesEffects {
  initiateAuth$: Observable<Action>;
  authCompleted$: Observable<Action>;
  initiateAuthorization$: Observable<Action>;
  authorizationCompleted$: Observable<Action>;
  initiateLogout$: Observable<Action>;
  performLogout$: Observable<Action>;
  logoutCompleted$: Observable<Action>;

  constructor(
    private actions$: Actions,
    private lifecyclesService: LifecyclesService,
    private userIdentity: UserIdentityService
  ) {
    this.initiateAuth$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.initiateAuth),
        concatMap(() =>
          this.lifecyclesService.executePreAuthHooks().pipe(
            map(() => AppLifecyclesActions.preAuthTasksCompleted()),
            catchError((error) => {
              return of(
                AppLifecyclesActions.lifecycleHookFailed({
                  phase: 'preAuth',
                  error,
                })
              );
            })
          )
        )
      )
    );

    this.authCompleted$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.authCompleted),
        concatMap(() =>
          this.lifecyclesService.executePostAuthHooks().pipe(
            map(() => AppLifecyclesActions.postAuthTasksCompleted()),
            catchError((error) => {
              return of(
                AppLifecyclesActions.lifecycleHookFailed({
                  phase: 'postAuth',
                  error,
                })
              );
            })
          )
        )
      )
    );

    this.initiateAuthorization$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.initiateAuthorization),
        concatMap(() =>
          this.lifecyclesService.executePreAuthorizationHooks().pipe(
            map(() => AppLifecyclesActions.preAuthorizationTasksCompleted()),
            catchError((error) => {
              return of(
                AppLifecyclesActions.lifecycleHookFailed({
                  phase: 'preAuthorization',
                  error,
                })
              );
            })
          )
        )
      )
    );

    this.authorizationCompleted$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.authorizationCompleted),
        concatMap(() =>
          this.lifecyclesService.executePostAuthorizationHooks().pipe(
            map(() => AppLifecyclesActions.postAuthorizationTasksCompleted()),
            catchError((error) => {
              return of(
                AppLifecyclesActions.lifecycleHookFailed({
                  phase: 'postAuthorization',
                  error,
                })
              );
            })
          )
        )
      )
    );

    this.initiateLogout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.initiateLogout),
        concatMap(() =>
          this.lifecyclesService.executePreLogoutHooks().pipe(
            map(() => AppLifecyclesActions.preLogoutTasksCompleted()),
            catchError((error) => {
              return of(
                AppLifecyclesActions.lifecycleHookFailed({
                  phase: 'preLogout',
                  error,
                })
              );
            })
          )
        )
      )
    );

    this.performLogout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.preLogoutTasksCompleted),
        tap(() => this.userIdentity.logout()),
        map(() => AppLifecyclesActions.logoutCompleted())
      )
    );

    this.logoutCompleted$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.logoutCompleted),
        concatMap(() =>
          this.lifecyclesService.executePostLogoutHooks().pipe(
            map(() => AppLifecyclesActions.postLogoutTasksCompleted()),
            catchError((error) => {
              return of(
                AppLifecyclesActions.lifecycleHookFailed({
                  phase: 'postLogout',
                  error,
                })
              );
            })
          )
        )
      )
    );
  }
}
